import { AuthContext, LoginOptions, LoginPhase, LoginResult } from '@/client/hooks/use-auth';
import { useWallet } from '@/client/hooks/use-wallet';
import { authTokenStore, setAuthToken, clearAuthToken, isUnexpiredToken } from '@/client/store/auth-token-store';
import { useModelStore } from '@/client/store/model-store';
import { usePendingPositionStore } from '@/client/store/pending-position-store';
import { useTradeResumeStore } from '@/client/store/trade-resume-store';
import { useTradeParamsStore } from '@/client/store/trade-store';
import { api } from '@/client/utils/api';
import { queryClient, persister } from '@/client/utils/query-client';
import { allowExplicitSiteLogin, isSiteSwitchInProgress } from '@/client/utils/site-switch';
import { createSiweMessage } from '@/client/utils/siwe';
import { useUserLogin } from '@/libs/react-sdk/api/use-user-login';
import { MouseEvent, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useStore } from 'zustand';

const apiUrl = import.meta.env.VITE_API_URL;
const workerProxyUrl = import.meta.env.VITE_WORKER_PROXY_API_URL;
const authBaseUrl = workerProxyUrl
  ? `${workerProxyUrl}/api/v1`
  : apiUrl === '/api'
    ? '/api/v1'
    : apiUrl?.replace(/\/v1\/?$/, '/v1');
type Attempt = {
  id: number;
  generation: number;
  account?: string;
  started?: boolean;
  switchStarted?: boolean;
  retryStarted?: boolean;
  modalSeen?: boolean;
  connecting?: boolean;
};
const cancelled = (error: unknown) => /reject|denied|cancel/i.test(String(error));

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const wallet = useWallet();
  const address = wallet.wallet?.address.toLowerCase();
  const token = useStore(authTokenStore, state => state.token);
  const tokenAccount = useStore(authTokenStore, state => state.account);
  const [invalidating, setInvalidating] = useState(false);
  const [loginPhase, setLoginPhase] = useState<LoginPhase>('idle');
  const [lastLoginResult, setLastLoginResult] = useState<LoginResult>();
  const generation = useRef(0);
  const sequence = useRef(0);
  const attempt = useRef<Attempt>();
  const clearing = useRef<Promise<void>>();
  const previousAddress = useRef(address);
  const previousToken = useRef(token);
  const latest = useRef({ wallet, address });
  latest.current = { wallet, address };
  const hasToken = isUnexpiredToken(token) && tokenAccount === address;
  const enabled = !invalidating && wallet.authSettled && wallet.isConnected && hasToken;
  const params = useMemo(() => ({ address: wallet.wallet?.address }), [wallet.wallet?.address]);
  const options = useMemo(() => ({ enabled }), [enabled]);
  const userLogin = useUserLogin(api, params, options);
  const { refetch: refetchUserLogin } = userLogin;
  const isAuthed = !invalidating && wallet.isConnected && hasToken && !!userLogin.data?.uid;
  const active = !['idle', 'authenticated', 'error'].includes(loginPhase);
  const isAuthLoading = !wallet.ready || invalidating || active || (enabled && userLogin.isLoading);
  const finish = useCallback((current: Attempt, outcome: LoginResult['outcome']) => {
    if (attempt.current !== current) return;
    attempt.current = undefined;
    setLoginPhase(outcome === 'success' ? 'authenticated' : outcome === 'error' ? 'error' : 'idle');
    setLastLoginResult({ attemptId: current.id, outcome });
  }, []);
  const clearSession = useCallback(() => {
    // Invalidate synchronously, before cancellation or any storage awaits.
    generation.current += 1;
    if (attempt.current) finish(attempt.current, 'cancelled');
    setLoginPhase('idle');
    setInvalidating(true);
    clearAuthToken();
    const models = useModelStore.getState();
    models.setWalletOpen(false);
    models.setDepositOpen(false);
    models.setSellOpen(false);
    models.setAuthOpen(false);
    usePendingPositionStore.getState().clear();
    if (!clearing.current) {
      clearing.current = (async () => {
        try {
          await queryClient.cancelQueries();
          queryClient.clear();
          await persister.removeClient();
        } finally {
          clearing.current = undefined;
          setInvalidating(false);
        }
      })();
    }
    return clearing.current;
  }, [finish]);
  const isCurrent = useCallback(
    (current: Attempt) =>
      attempt.current === current &&
      generation.current === current.generation &&
      (!current.account || latest.current.address === current.account) &&
      latest.current.wallet.rawIsConnected,
    []
  );

  useLayoutEffect(() => {
    const changed = previousAddress.current !== undefined && previousAddress.current !== address;
    previousAddress.current = address;
    const tokenRemoved = !!previousToken.current && !token;
    previousToken.current = token;
    const invalidToken = !!token && (!isUnexpiredToken(token) || (!!address && tokenAccount !== address));
    if (changed || tokenRemoved || invalidToken) {
      if (changed && address) toast.error(t('signer-account-mismatch'));
      void clearSession().catch(() => toast.error(t('sign-in-failed')));
    }
  }, [address, token, tokenAccount, clearSession, t]);

  const login = useCallback(
    (source?: LoginOptions | MouseEvent) => {
      if (attempt.current || clearing.current || isSiteSwitchInProgress()) return;
      allowExplicitSiteLogin();
      const current: Attempt = { id: ++sequence.current, generation: generation.current, account: latest.current.address };
      attempt.current = current;
      setLastLoginResult(undefined);
      if (source && 'source' in source && source.source === 'trade') {
        const { type, marketAddress, optionAddress, optionSide, amount } =
          source.tradeParams ?? useTradeParamsStore.getState();
        useTradeResumeStore.getState().suspendTrade({
          attemptId: current.id,
          route: window.location.pathname + window.location.search,
          params: { type, marketAddress, optionAddress, optionSide, amount },
        });
      }
      useModelStore.getState().setTradeOpen(false);
      useModelStore.getState().setSellOpen(false);
      const w = latest.current.wallet;
      setLoginPhase(w.rawIsConnected ? (w.wrongNetwork ? 'switching-chain' : 'loading-client') : 'connecting');
      if (!w.rawIsConnected) {
        const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (mobile && 'ethereum' in window && w.connectInjected) {
          current.connecting = true;
          void w
            .connectInjected()
            .catch(error => {
              if (attempt.current === current) {
                finish(current, cancelled(error) ? 'cancelled' : 'error');
                toast.error(t('sign-in-failed'));
              }
            })
            .finally(() => {
              current.connecting = false;
            });
        } else if (w.openConnectModal) {
          w.openConnectModal();
        } else {
          finish(current, 'error');
          toast.error(t('sign-in-failed'));
        }
      }
    },
    [finish, t]
  );

  useEffect(() => {
    const current = attempt.current;
    if (!current || invalidating) return;
    if (wallet.connectModalOpen) current.modalSeen = true;
    if (!wallet.rawIsConnected) {
      // Only an observed picker close can cancel. Client hydration is unrelated.
      if (current.modalSeen && !wallet.connectModalOpen && !wallet.isConnecting && !current.connecting)
        finish(current, 'cancelled');
      return;
    }
    if (!current.account) current.account = address;
    if (!isCurrent(current) || !address) return;
    if (wallet.wrongNetwork) {
      setLoginPhase('switching-chain');
      if (!current.switchStarted) {
        current.switchStarted = true;
        if (!wallet.switchToAppChain) {
          finish(current, 'error');
          return;
        }
        void wallet.switchToAppChain().catch(error => {
          if (!isCurrent(current)) return;
          finish(current, cancelled(error) ? 'cancelled' : 'error');
          toast.error(t('wrong-network'));
        });
      }
      return;
    }
    if (wallet.walletClientError && !wallet.walletClientLoading) {
      if (!current.retryStarted && wallet.retryWalletClient) {
        current.retryStarted = true;
        void wallet
          .retryWalletClient()
          .then(result => {
            if (isCurrent(current) && result && typeof result === 'object' && 'error' in result && result.error) {
              finish(current, 'error');
              toast.error(t('wallet-client-retry'));
            }
          })
          .catch(() => {
            if (isCurrent(current)) {
              finish(current, 'error');
              toast.error(t('wallet-client-retry'));
            }
          });
      } else if (!current.retryStarted) {
        finish(current, 'error');
        toast.error(t('wallet-client-retry'));
      }
      return;
    }
    if (!wallet.walletClient || !wallet.authSettled) {
      setLoginPhase('loading-client');
      return;
    }
    if (current.started) return;
    current.started = true;
    const client = wallet.walletClient;
    void (async () => {
      try {
        if (!hasToken) {
          setLoginPhase('signing');
          const nonceRes = await fetch(`${authBaseUrl}/auth/nonce`).then(r => r.json());
          if (!isCurrent(current)) return;
          if (!nonceRes.data?.nonce) throw new Error('Missing nonce');
          const message = createSiweMessage({ address, nonce: nonceRes.data.nonce });
          const signature = await client.signMessage({ message, account: address as `0x${string}` });
          if (!isCurrent(current)) return;
          setLoginPhase('verifying');
          const verification = await fetch(`${authBaseUrl}/auth/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message, signature, address }),
          }).then(r => r.json());
          if (!isCurrent(current)) return;
          if (verification.code !== 0 || !isUnexpiredToken(verification.data?.token)) throw new Error('Verification failed');
          setAuthToken(verification.data.token, address);
        }
        const result = await refetchUserLogin();
        if (!isCurrent(current)) return;
        if (!result.data?.uid || result.error) throw new Error('Login failed');
        if (result.data.isNewUser) useModelStore.getState().setAuthOpen(true);
        finish(current, 'success');
        toast.success(t('sign-in-success'));
      } catch (error) {
        if (!isCurrent(current)) return;
        finish(current, cancelled(error) ? 'cancelled' : 'error');
        toast.error(t('sign-in-failed'));
      }
    })();
  }, [wallet, address, invalidating, hasToken, loginPhase, finish, isCurrent, refetchUserLogin, t]);

  const logout = useCallback(
    async (options?: { silent?: boolean }) => {
      useTradeResumeStore.getState().discardSuspendedTrade();
      useModelStore.getState().setTradeOpen(false);
      const clear = clearSession();
      await Promise.all([clear, latest.current.wallet.disconnect()]);
      setLoginPhase('idle');
      if (!options?.silent) toast.success(t('logout-success'));
    },
    [clearSession, t]
  );
  useEffect(() => {
    if (userLogin.error?.cause === 401) void clearSession();
  }, [userLogin.error, clearSession]);
  useEffect(
    () => () => {
      generation.current += 1;
      attempt.current = undefined;
    },
    []
  );
  const data = isAuthed ? userLogin.data : undefined;
  const phase = isAuthed && !active ? 'authenticated' : loginPhase;
  const value = useMemo(
    () => ({ isAuthed, isAuthLoading, data, login, logout, loginPhase: phase, lastLoginResult }),
    [isAuthed, isAuthLoading, data, login, logout, phase, lastLoginResult]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
